import React from 'react';
import AppHeader from './AppHeader';
import LegalFooter from './LegalFooter';

const sectionTitleClasses = 'text-xl font-semibold text-slate-800';
const paragraphClasses = 'mt-2 text-sm leading-7 text-slate-600';

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <AppHeader />
      <main className="mx-auto max-w-4xl px-4 py-10">
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-10">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-pink-500">Privacy Policy</p>
          <h1 className="mt-3 text-3xl font-semibold text-slate-900">Privacy Policy</h1>
          <p className="mt-3 text-sm text-slate-500">Effective July 24, 2026</p>

          <section className="mt-8">
            <h2 className={sectionTitleClasses}>What we collect</h2>
            <p className={paragraphClasses}>
              We collect the information you give us directly, including your Google sign-in information, email address,
              pet name, pet type, questionnaire answers, uploaded pet photos, generated images and videos, share links,
              and subscription status. If you choose email updates by sending the pre-addressed message offered in the
              app, we also receive the sender address and basic email metadata needed to confirm your choice and keep our
              replies in the same email thread.
            </p>
          </section>

          <section className="mt-8">
            <h2 className={sectionTitleClasses}>How we use it</h2>
            <p className={paragraphClasses}>
              We use this information to create your generated media, save your results, let you return to your account,
              process subscriptions, prevent abuse, review uploaded content for safety and eligibility, improve the
              product, and understand how people use Fluffball. If you expressly request email updates, we use your email
              address to send result notices and occasional Fluffball follow-ups, which may include promotional offers.
            </p>
          </section>

          <section className="mt-8">
            <h2 className={sectionTitleClasses}>AI processing and third-party services</h2>
            <p className={paragraphClasses}>
              When you create a result, Fluffball sends the pet photos you selected, along with related inputs such as pet
              type, pet name, and questionnaire answers, to Fluffball&apos;s servers and trusted third-party providers that
              help us run the service. These providers include Firebase and Google Cloud for authentication, storage, and
              infrastructure; OpenAI for moderation, input-safety checks, and image generation; Fal for image and video
              generation; Gemini (Google) for pet description generation, content moderation, and verification that
              uploaded photos show the same pet; RevenueCat and Apple for in-app subscription management on iOS; Stripe
              for web billing and subscription management; Resend for sending and receiving requested email updates;
              Mixpanel for product analytics; and AppsFlyer plus advertising partners such as Meta to measure mobile app
              installs, subscription conversions, and campaign performance. These providers may process your information
              only as needed to deliver, measure, or support the service.
            </p>
          </section>

          <section className="mt-8">
            <h2 className={sectionTitleClasses}>Analytics</h2>
            <p className={paragraphClasses}>
              We log product usage events to understand the flow through the app, improve reliability, and measure core
              actions such as sign-in, uploads, subscriptions, sharing, downloads, and result views.
            </p>
          </section>

          <section className="mt-8">
            <h2 className={sectionTitleClasses}>Advertising attribution on iOS</h2>
            <p className={paragraphClasses}>
              On the iOS app, we may ask for permission to use your device identifier for advertising attribution. If you
              allow tracking, we use AppsFlyer and partners such as Meta to understand which ads led to an install,
              subscription, or other conversion event. If you do not allow tracking, core Fluffball features still work,
              and we may still receive aggregated attribution data such as SKAdNetwork reporting.
            </p>
          </section>

          <section className="mt-8">
            <h2 className={sectionTitleClasses}>Retention</h2>
            <p className={paragraphClasses}>
              We keep account and result information for as long as needed to operate the service, support returning users,
              comply with legal obligations, and enforce our terms. Generated media and uploaded pet photos may remain tied
              to your account so future generations can use them. Pending email-confirmation tokens expire automatically
              for use even if retained temporarily for security and reliability. Confirmed email preferences and related
              delivery records are retained until no longer needed or your account is deleted, subject to legal obligations.
            </p>
          </section>

          <section className="mt-8">
            <h2 className={sectionTitleClasses}>Your choices</h2>
            <p className={paragraphClasses}>
              You can sign out at any time, manage or cancel your subscription through Apple or Stripe depending on where
              you purchased it, unsubscribe from email updates using the link in any Fluffball email, and contact us through
              the support page if you need help with account or privacy requests. Closing the pre-addressed email without
              sending it does not opt you in. Deleting your account removes the email opt-in records associated with it.
            </p>
          </section>

          <section className="mt-8">
            <h2 className={sectionTitleClasses}>Changes</h2>
            <p className={paragraphClasses}>
              We may update this policy from time to time. When we do, we will update the effective date on this page.
            </p>
          </section>
        </div>
      </main>
      <LegalFooter />
    </div>
  );
};

export default PrivacyPolicyPage;
